import { useCallback } from 'react';
import type { Område, OmrådeParentType } from '@origo/seksjon';
import {
    useOmråder as useOmråderQuery,
    useCreateOmråde,
    useUpdateOmråde,
    useDeleteOmråde,
} from '@origo/seksjon';

interface ParentRef {
    parentType?: OmrådeParentType;
    parentId?: string;
}

interface UseOmråderReturn {
    områder: Område[];
    loading: boolean;
    error: string | null;
    reload: () => void;
    addOmråde: (navn: string, parent?: ParentRef) => Promise<void>;
    updateOmråde: (
        objectId: number,
        navn: string,
        parent?: ParentRef | null
    ) => Promise<void>;
    deleteOmråde: (objectId: number, områdeId: number) => Promise<void>;
}

/**
 * Admin-orientert wrapper rundt @origo/seksjon's område-hooks. Normaliserer
 * input til mutation-input-shape og eksponerer samme public API som
 * den tidligere service-context-baserte hooken.
 *
 * Query-invalidering håndteres av mutation-hookene automatisk — ingen
 * `reload` trengs etter mutate, men vi eksponerer `refetch` som fortsatt
 * metode for bakoverkompatibilitet.
 */
export function useOmråder(planId: string): UseOmråderReturn {
    const query = useOmråderQuery(planId || undefined);
    const createMutation = useCreateOmråde();
    const updateMutation = useUpdateOmråde();
    const deleteMutation = useDeleteOmråde();

    const addOmråde = useCallback(
        async (navn: string, parent?: ParentRef) => {
            await createMutation.mutateAsync({
                navn,
                planId,
                parentType: parent?.parentType,
                parentId: parent?.parentId,
            });
        },
        [createMutation, planId]
    );

    const updateOmråde = useCallback(
        async (objectId: number, navn: string, parent?: ParentRef | null) => {
            // null tømmer forelderen eksplisitt; undefined lar den stå.
            const patch: Parameters<typeof updateMutation.mutateAsync>[0] = {
                objectId,
                navn,
                planId,
            };
            if (parent !== undefined) {
                patch.parentType = parent?.parentType;
                patch.parentId = parent?.parentId;
            }
            await updateMutation.mutateAsync(patch);
        },
        [updateMutation, planId]
    );

    const deleteOmråde = useCallback(
        async (objectId: number, områdeId: number) => {
            await deleteMutation.mutateAsync({
                objectId,
                områdeId: String(områdeId),
                planId,
            });
        },
        [deleteMutation, planId]
    );

    return {
        områder: query.data ?? [],
        loading: query.isLoading,
        error: query.error instanceof Error ? query.error.message : null,
        reload: () => {
            query.refetch();
        },
        addOmråde,
        updateOmråde,
        deleteOmråde,
    };
}
